import React from "react";
import { Tabs, Tooltip } from "antd";
import { EyeOutlined, EyeTwoTone } from '@ant-design/icons';
import Panel from "../../../../../components/Panel";
import Table from "../../../../../components/EditableTable";
import { createPortal } from "react-dom";
import { getPanelMaxHeight, getPanelMaxWidth, getPanelWidth } from "../../../../../services/helper/panelSizeHelper";
import { getVictimSummaryData } from "../../../../../services/Sierra/SierraCtrl";
import { getAggressorSignalNets } from "../../../../../services/Sierra/helper/victimAggressorSearchHelper";
import "./index.css";

// [{signalName, vpp, victimNets: {victimNet, signalNets}, signalNets,  aggressorNets[signal,nets]]}]
class VictimSummary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      maxHeight: 600,
      maxWidth: 800,
      activePcb: "",
      victimSummaryData: [],
      tableData: []
    }

    this.dialogRoot = document.getElementById('root');
  }

  componentDidMount = () => {
    this.initData()
    window.addEventListener('resize', this.resize);
    this.resize();

    this.signalNetsColumns = [
      {
        title: () => {
          const { tableData } = this.state;
          const { selectedVictimSignals = [] } = this.props;
          const isAllSelected = tableData.every(it => selectedVictimSignals.includes(it.signalName));
          return <span className="signal-name-box">
            Signal Name
            <Tooltip title={isAllSelected ? "Hide Curve" : "Show Curve"}>
              <span className="signal-name-eye-icon" onClick={() => this.changeSelectedVictimSignals(null, isAllSelected)}>
                {isAllSelected ? <EyeTwoTone /> : <EyeOutlined />}
              </span>
            </Tooltip>
          </span>
        },
        dataIndex: "signalName",
        key: "signalName",
        render: (signalName) => {
          const { selectedVictimSignals = [] } = this.props;
          return <span className="signal-name-box">
            {signalName}
            <Tooltip title={selectedVictimSignals.includes(signalName) ? "Hide Curve" : "Show Curve"}>
              <span className="signal-name-eye-icon" onClick={() => this.changeSelectedVictimSignals(signalName)}>
                {selectedVictimSignals.includes(signalName) ? <EyeTwoTone /> : <EyeOutlined />}
              </span>
            </Tooltip>
          </span>
        }
      },
      {
        title: "Victim Nets",
        dataIndex: "signalNets",
        key: "signalNets",
        render: (_, record) => {
          const { signalNets = [], victimAggressors = [] } = record;
          const victimNets = victimAggressors.map(it => it.victimNet).filter(it => signalNets.includes(it));
          const otherNets = signalNets.filter(it => !victimNets.includes(it));
          return (<>
            <span className="victim-net">{victimNets.join(", ")}</span>
            <span>{otherNets.length ? ", " : ""}{otherNets.join(", ")}</span>
          </>)
        }
      },
      {
        title: "Aggressor Signal",
        dataIndex: "victimAggressors",
        key: "victimAggressors",
        render: (_, record) => {
          const signalNetsObj = getAggressorSignalNets(record.victimAggressors);
          return (<>
            {Object.keys(signalNetsObj).map((signal) => (
              <div key={`${signal}-${signalNetsObj[signal][0]}`}>
                {signal}
                {/* <span className='victim-net'>{signal}</span> */}
                {/* <span> ({signalNetsObj[signal].join(", ")}) </span> */}
              </div>
            ))}</>)
        }
      },
      {
        title: "Peak-to-Peak (mV)",
        dataIndex: "vpp",
        key: "vpp",
        sorter: (a, b) => {
          return Number(a.vpp) - Number(b.vpp);
        }
      }
    ]

  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  resize = () => {
    const offset = this.dialogRoot.getBoundingClientRect();
    this.setState({
      maxHeight: getPanelMaxHeight(offset, 600) - 80,
      maxWidth: getPanelMaxWidth(offset, 800)
    })
  }

  initData = () => {
    const { verificationId } = this.props;
    getVictimSummaryData(verificationId).then((res) => {
      if (!res || !res.length) return;
      const tableData = res[0] && res[0].victims ? res[0].victims : [];
      tableData.sort((a, b) => Number(a.vpp) - Number(b.vpp));
      this.setState({
        victimSummaryData: res,
        activePcb: res[0] && res[0].pcbId ? res[0].pcbId : "",
        tableData
      })
    })
  }

  changeActivePcb = (activePcb) => {
    const { victimSummaryData } = this.state;
    const tableData = (victimSummaryData.find((item) => item.pcbId === activePcb) || {}).victims || [];
    tableData.sort((a, b) => Number(a.vpp) - Number(b.vpp));
    this.setState({
      activePcb,
      tableData
    });
  }

  changeSelectedVictimSignals = (signalName, isAllSelected) => {
    const { selectedVictimSignals = [] } = this.props;
    if (signalName === null) {
      const selectedRowKeys = isAllSelected ? [] : this.state.tableData.map(it => it.signalName);
      this.props.changeselectedVictimSignals(selectedRowKeys);
      return;
    }
    const selectedRowKeys = selectedVictimSignals.includes(signalName) ? selectedVictimSignals.filter(it => it !== signalName) : [...selectedVictimSignals, signalName];
    this.props.changeselectedVictimSignals(selectedRowKeys);
  }

  closeModal = () => {
    this.props.changeVictimSummaryVisible(false);
  }

  render() {
    const { maxHeight, maxWidth, activePcb, victimSummaryData, tableData } = this.state;
    const { allDesigns } = this.props;
    const content = <Panel
      title="Victim Summary"
      className='sierra-measure-panel sierra-panel'
      onCancel={this.closeModal}
      zIndex={1000}
      width={getPanelWidth(maxWidth, { defaultWidth: 800 })}
      maxHeight={maxHeight}
      position='panel-center-top'
      draggable
      minHeight={100}
      minWidth={600}
      maxWidth={maxWidth}
    >
      <div className="sierra-victim-summary-panel-content">
        <Tabs
          onChange={this.changeActivePcb}
          activeKey={activePcb}
          items={victimSummaryData.map((item) => ({ key: item.pcbId, label: (allDesigns.find((pcb) => pcb.PCBID === item.pcbId) || {}).name }))}
        />
        <Table
          className='victim-summary-table'
          rowKey={(record) => record.signalName}
          dataSource={tableData}
          columns={this.signalNetsColumns}
          scroll={tableData.length > 11 ? { y: 517 } : {}}
          showSorterTooltip={false}
        />
      </div>
    </Panel>
    return createPortal(content, this.dialogRoot);
  }
}

export default VictimSummary;